import React, { useEffect, useState } from 'react';
import GetPaidCard from '@components/dashboard/components/GetPaidCard';
import { useBilling } from '@context/BillingContext';
import { SUBSCRIPTION_PLANS } from '@utils/constants';
import type { BillingInterval, PlanChangePreview, PlanTier } from '@/types/photographer';

const PLAN_TIERS: PlanTier[] = ['basic', 'pro', 'enterprise'];

const STATUS_LABEL: Record<string, string> = {
  trialing: 'Free trial',
  active: 'Active',
  past_due: 'Payment past due',
  canceled: 'Canceled',
  unpaid: 'Unpaid',
  incomplete: 'Incomplete',
  incomplete_expired: 'Expired',
  suspended: 'Suspended',
};

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const ChangePlanCard: React.FC<{ currentPlan?: PlanTier; currentInterval?: BillingInterval }> = ({
  currentPlan,
  currentInterval,
}) => {
  const { previewChange, updateSubscription } = useBilling();
  const [plan, setPlan] = useState<PlanTier>(currentPlan ?? 'basic');
  const [interval, setInterval] = useState<BillingInterval>(currentInterval ?? 'month');
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  const [busy, setBusy] = useState(false);

  const handlePreview = async (): Promise<void> => {
    setBusy(true);
    const result = await previewChange(plan, interval);
    setPreview(result);
    setBusy(false);
  };

  const handleConfirm = async (): Promise<void> => {
    setBusy(true);
    await updateSubscription(plan, interval);
    setPreview(null);
    setBusy(false);
  };

  const resetPreview = (): void => setPreview(null);

  return (
    <div className="rounded-lg border border-gray-200 p-5">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Change plan</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="change-plan-tier" className="block text-sm text-gray-500 mb-1">
            New plan
          </label>
          <select
            id="change-plan-tier"
            value={plan}
            onChange={(e) => {
              setPlan(e.target.value as PlanTier);
              resetPreview();
            }}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            {PLAN_TIERS.map((tier) => (
              <option key={tier} value={tier}>
                {SUBSCRIPTION_PLANS[tier].name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="change-plan-interval" className="block text-sm text-gray-500 mb-1">
            Billing interval
          </label>
          <select
            id="change-plan-interval"
            value={interval}
            onChange={(e) => {
              setInterval(e.target.value as BillingInterval);
              resetPreview();
            }}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="month">Monthly</option>
            <option value="year">Annual</option>
          </select>
        </div>
      </div>

      {preview && (
        <div className="mt-4 rounded-md bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <p className="font-medium">{preview.message}</p>
          <p className="mt-1">
            {preview.proration_amount >= 0
              ? `Amount due now: $${preview.proration_amount.toFixed(2)}`
              : `Credit applied: $${Math.abs(preview.proration_amount).toFixed(2)}`}
          </p>
          <p>New rate: ${preview.new_monthly_amount.toFixed(2)}/mo</p>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-3">
        {preview ? (
          <button
            onClick={() => handleConfirm()}
            disabled={busy}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Confirm change
          </button>
        ) : (
          <button
            onClick={() => handlePreview()}
            disabled={busy}
            className="rounded-md border border-blue-600 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          >
            Preview change
          </button>
        )}
      </div>
    </div>
  );
};

const Billing: React.FC = () => {
  const { current, loading, error, refresh, openPortal, startCheckout, cancel, reactivate } =
    useBilling();

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hasSub = current?.has_subscription;
  const plan = current?.plan as PlanTier | undefined;
  const planName = plan ? SUBSCRIPTION_PLANS[plan].name : null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow-lg rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Billing &amp; Subscription</h1>

        <GetPaidCard />

        {error && (
          <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {loading && !current && <p className="text-sm text-gray-500">Loading…</p>}

        {current && !hasSub && (
          <div className="text-center py-10">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Choose a plan</h3>
            <p className="text-sm text-gray-500 mb-6">
              Subscribe to activate your account. Your card is charged when you subscribe.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              {(Object.keys(SUBSCRIPTION_PLANS) as PlanTier[]).map((tier) => {
                const p = SUBSCRIPTION_PLANS[tier];
                return (
                  <div key={tier} className="rounded-lg border border-gray-200 p-4 text-left">
                    <div className="font-semibold text-gray-900">{p.name}</div>
                    <div className="text-2xl font-bold text-gray-900 my-2">
                      ${p.monthlyPrice.toFixed(2)}
                      <span className="text-sm font-normal text-gray-500">/mo</span>
                    </div>
                    <button
                      onClick={() => startCheckout(p.planId, 'month')}
                      className="mt-2 w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      Subscribe
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {current && hasSub && (
          <div className="space-y-6">
            <div className="rounded-lg border border-gray-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">Current plan</div>
                  <div className="text-xl font-semibold text-gray-900">{planName ?? plan}</div>
                </div>
                <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                  {STATUS_LABEL[current.status ?? ''] ?? current.status}
                </span>
              </div>

              <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
                {current.status === 'trialing' && (
                  <div>
                    <dt className="text-gray-500">Free trial ends</dt>
                    <dd className="text-gray-900">{formatDate(current.trial_end)}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-gray-500">
                    {current.cancel_at_period_end ? 'Access until' : 'Renews on'}
                  </dt>
                  <dd className="text-gray-900">{formatDate(current.current_period_end)}</dd>
                </div>
              </dl>

              {current.cancel_at_period_end && (
                <p className="mt-3 rounded-md bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                  Your subscription is set to cancel at the end of the current period.
                </p>
              )}
            </div>

            <ChangePlanCard currentPlan={plan} currentInterval={current.interval} />

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => openPortal()}
                className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                Manage billing
              </button>
              {current.cancel_at_period_end ? (
                <button
                  onClick={() => reactivate()}
                  className="rounded-md border border-blue-600 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50"
                >
                  Reactivate subscription
                </button>
              ) : (
                <button
                  onClick={() => cancel()}
                  className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  Cancel subscription
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400">
              Invoices and payment methods are managed in the secure Stripe billing portal.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;
